<template>
  <base-modal :title="boardName + ' properties'" @close-modal="onCloseModal">
    <div class="wrapper clearfix">
      <table class="tbl">
        <tbody>
          <tr>
            <td class="left">Board ID</td>
            <td class="right">
              {{ board }}
            </td>
          </tr>
          <tr v-if="!isFolder">
            <td class="left">Custom ID</td>
            <td class="right">
              <input
                ref="id_input"
                :disabled="!canEdit"
                placeholder="Add custom board id..."
                spellcheck="false"
                class="id"
                :value="customId"
                @blur="onBlurInput"
                @keyup.enter="$event.target.blur()"
              />
            </td>
          </tr>
          <tr v-if="!isFolder">
            <td class="left">Content</td>
            <td class="right">
              <span>{{ elements.length }}</span> element{{
                elements.length !== 1 ? 's' : ''
              }}, <span>{{ notes.length }}</span> note{{
                notes.length !== 1 ? 's' : ''
              }}, <span>{{ jumpers.length }}</span> jumper{{
                jumpers.length !== 1 ? 's' : ''
              }}, <span>{{ branches.length }}</span> branch{{
                branches.length !== 1 ? 'es' : ''
              }}, <span>{{ commentsNumber }}</span> comment{{
                commentsNumber !== 1 ? 's' : ''
              }}
            </td>
          </tr>
          <tr v-if="!isFolder">
            <td class="left">
              <span
                v-tooltip="{
                  content: 'Only counting words in<br/> elements and labels',
                  html: true,
                }"
              >
                Word count
              </span>
            </td>
            <td class="right">
              <span>
                {{ wordCount }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="!isFolder">
        <hr />
        <div class="label">Components referenced in this board</div>
        <div v-if="components.length >= 1">
          <component-tag
            v-for="(component, index) in components"
            :key="index"
            :item="component"
          />
        </div>
        <div v-else class="empty">No components referenced</div>
      </div>
    </div>
  </base-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import ComponentTag from './ComponentTag.vue';
import { countWords, has } from '../../../utils.js';
import BaseModal from '../BaseModal.vue';

export default {
  components: {
    ComponentTag,
    BaseModal,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return { canEdit };
  },

  computed: {
    ...mapGetters('application/modals', ['getBoardPropertiesModal']),
    ...mapGetters('project/data/boards', [
      'getContainedNodes',
      'getNode',
      'getIsRecursiveContainer',
    ]),
    ...mapGetters('project/data/components', {
      getComponent: 'getNode',
    }),

    /**
     * Return the board id for which to show the board properties
     * @return {String}     The board id
     */
    board() {
      return this.getBoardPropertiesModal;
    },

    /**
     * Check if the board for which we show properties is a folder
     * @return {Boolean}    True if is folder
     */
    isFolder() {
      return this.getIsRecursiveContainer(this.board);
    },

    /**
     * Return all elements of the board
     * @returns {Array}     The board elements
     */
    elements() {
      return this.getContainedNodes(this.board, 'elements', 'elements');
    },

    /**
     * Return all connections of the board
     * @returns {Array}     The board connections
     */
    connections() {
      return this.getContainedNodes(this.board, 'connections', 'connections');
    },

    /**
     * Return all notes of the board
     * @returns {Array}     The board notes
     */
    notes() {
      return this.getContainedNodes(this.board, 'notes', 'notes');
    },

    /**
     * Return all jumpers of the board
     * @returns {Array}     The board jumpers
     */
    jumpers() {
      return this.getContainedNodes(this.board, 'jumpers', 'jumpers');
    },

    /**
     * Return all branches of the board
     * @returns {Array}     The board branches
     */
    branches() {
      return this.getContainedNodes(this.board, 'branches', 'branches');
    },

    /**
     * Return all comments of the board
     * @returns {Array}     The board comments
     */
    commentsNumber() {
      const board = this.getNode(this.board);
      if (!board.comments) return 0;
      return board.comments.length;
    },

    /**
     * Return all the referenced components in the board
     * @returns {Array}    The components
     */
    components() {
      // Transfer all component lists from all elements to a new array
      const allComponents = this.elements.map(element => element.components);

      // Remove duplicates
      const flat = _.uniqBy(allComponents.flat(), 'component');

      // Retrieve the component from each id and remove the attributes property
      const components = flat
        .map(component => this.getComponent(component.component))
        .map(({ attributes, ...keepAttrs }) => keepAttrs);

      return components;
    },

    /**
     * Return the value of the board's custom Id property
     * @returns {String}     The custom id or null
     */
    customId() {
      if (!this.board) return null;

      const board = this.getNode(this.board);
      if (has(board, 'customId')) {
        return board.customId;
      }
      return null;
    },

    /**
     * Return the name of the board
     * @returns {String}
     */
    boardName() {
      return this.getNode(this.board).name;
    },

    /**
     * Return the number of words in this board
     * @return {number}     The number of words in the board
     */
    wordCount() {
      let count = 0;
      // Count the words in title and content of each board element
      this.elements.forEach(element => {
        const { title, content } = element;
        count += countWords(title) + countWords(content);
      });
      // Count the words in label of each board connection
      this.connections.forEach(connection => {
        const { label } = connection;
        count += countWords(label);
      });
      return count;
    },
  },

  methods: {
    ...mapActions('application/modals', ['setBoardPropertiesModal']),
    ...mapActions('project/data/boards', ['customUpdate', 'deleteProperty']),

    /**
     * Event listener for pressing the close button
     */
    onCloseModal() {
      this.setBoardPropertiesModal({ value: null });
    },

    /**
     * Event listener for blur event on the custom board id input
     */
    onBlurInput() {
      const input = this.$refs.id_input;
      const id = this.board;
      if (input.value) {
        this.customUpdate({
          id,
          props: { customId: input.value },
          apply: true,
        });
      } else if (this.customId) {
        this.deleteProperty({ id, prop: 'customId', apply: true });
      }
    },
  },
};
</script>
<style scoped lang="scss">
.wrapper {
  padding: 0 20px 20px;
  .label {
    color: #828585;
    font-size: 15px;
    padding: 20px 0;
  }
  .empty {
    // background: #ccc;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 60px;
    border: 1px solid #41494b;
  }
  &.clearfix::after {
    content: '';
    clear: both;
    display: table;
  }
}

.tbl {
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;
  margin: 15px 0 10px 0;
  font-size: 15px;
  table-layout: fixed;

  tr {
    vertical-align: top;

    td {
      padding: 0;
      height: 50px;
      text-align: left;
      vertical-align: middle;
      box-sizing: border-box;
      &.right span {
        color: #fff;
        font-weight: 600;
      }
      &.left {
        color: #828585;
        width: 100px;
      }
    }
  }
}

.modal input.id {
  background: #252929;
  padding: 10px 12px 11px;
  border: none;
  border-radius: 3px;
  width: 100%;
  font-size: 14px;
  color: #bcbcbc;
  font-family: 'open sans';
}

hr {
  border: none;
  border-top: 1px solid #3c4041;
}
</style>
